import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyGetDocumentDownloadUrlQuery } from '@features/patient-documents';
import { useDebounce } from '@hooks/useDebounce';
import {
  Alert,
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';

import {
  type TimelineLens,
  usePatientTimeline,
  useUploadPatientArtifacts,
} from '../../store/hooks';
import type {
  DocumentEnvelopePayload,
  TimelineEnvelope,
} from '../../types';

import { ArtifactDropOverlay } from './ArtifactDropOverlay';
import {
  getDateFilters,
  KIND_FILTERS,
  LENS_FILTERS,
} from './constants';
import { DocRefDrawer } from './DocRefDrawer';
import { EnvelopeRow } from './EnvelopeRow';
import {
  envelopeSortDate,
  groupByPeriod,
  isGeneratedDoc,
} from './helpers';
import {
  type FilterState,
  TimelineFilterStrip,
} from './TimelineFilterStrip';
import { TimelineThread } from './TimelineThread';
import { UploadingCard } from './UploadingCard';
import { useChartWideDropZone } from './useChartWideDropZone';

interface PendingUpload {
  artifactId: string;
  filename: string;
  status: string;
  uploadedAt: number;
}

/**
 * How long we keep an unsettled ghost card around when no DocRef ever
 * arrives. Mostly a safety net for failed processing — well above the
 * happy-path 30s pipeline so we don't yank healthy ghosts.
 */
const PENDING_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

interface PatientTimelineViewProps {
  patientId: string;
  patientName?: string;
  /** Render the chart-wide drop overlay when a file is dragged on-window. */
  enableDropZone?: boolean;
}

/**
 * Patient timeline — primary read view. Driven by the unified envelope
 * endpoint (`GET /v2/provider/patients/{id}/timeline`). Renders:
 *
 * - A 3-row filter strip (lens / type / when + search)
 * - Roots in `items` (newest-first), encounter envelopes wrapped in
 *   TimelineThread with their server-supplied children nested oldest-first
 * - DocRef cards for non-encounter roots
 * - Skeleton "ghost" cards for in-flight uploads (keyed off
 *   source_artifact_id), shown until SSE-driven invalidation refetches
 *   and the real DocRefs land
 * - A right-side drawer for DocRef detail
 *
 * All filtering (lens, doc_kinds, date range, full-text search) is
 * server-side as of GDN-938 v1.1. The hook re-fetches when any filter
 * input changes; the search input is debounced 300ms to avoid a request
 * per keystroke.
 */
export function PatientTimelineView({
  patientId,
  patientName,
  enableDropZone = true,
}: PatientTimelineViewProps) {
  const [filterState, setFilterState] = useState<FilterState>({
    lensId: 'all',
    kindId: 'all',
    dateId: 'all',
    query: '',
  });
  const [openDocRefId, setOpenDocRefId] = useState<string | null>(null);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);

  // Resolve UI filter state → API params. Memoized so the hook only sees
  // a new args object when something actually changed.
  const dateFilters = useMemo(() => getDateFilters(), []);
  const debouncedQuery = useDebounce(filterState.query.trim(), 300);

  const apiArgs = useMemo(() => {
    const kindSpec = KIND_FILTERS.find(k => k.id === filterState.kindId);
    const dateSpec = dateFilters.find(d => d.id === filterState.dateId);
    return {
      patientId,
      lens: filterState.lensId as TimelineLens,
      docKinds: kindSpec?.kinds ?? null,
      since: dateSpec?.since ?? null,
      before: dateSpec?.before ?? null,
      q: debouncedQuery || null,
    };
  }, [
    patientId,
    filterState.lensId,
    filterState.kindId,
    filterState.dateId,
    debouncedQuery,
    dateFilters,
  ]);

  const { items, threads, isLoading, isFetching, error } =
    usePatientTimeline(apiArgs);

  const { upload, isUploading, error: uploadError } =
    useUploadPatientArtifacts(patientId);

  const grouped = useMemo(
    () => groupByPeriod(items, envelopeSortDate),
    [items],
  );

  // Per-kind counts would require a separate unfiltered call; v1 omits
  // them. The strip falls back to label-only chips when count is 0.
  const kindCounts = useMemo<Record<string, number>>(() => ({}), []);

  const totalShown = items.length;

  const handleDrop = useCallback(
    async (files: File[]) => {
      const response = await upload(files);
      if (!response?.artifacts?.length) return;
      const now = Date.now();
      setPendingUploads(prev => [
        ...response.artifacts.map(a => ({
          artifactId: a.artifact_id,
          filename: a.original_filename ?? 'Untitled',
          status: a.processing_status ?? 'uploaded',
          uploadedAt: now,
        })),
        ...prev,
      ]);
    },
    [upload],
  );

  // Drop any pending upload whose source_artifact_id already shows up as a
  // DocRef anywhere in the response (root items or encounter thread
  // children). First DocRef wins — subsequent multi-record splits just
  // arrive alongside. Also expire ancient pendings (see constant above).
  useEffect(() => {
    const seenSourceArtifactIds = new Set<string>();
    const collect = (env: TimelineEnvelope) => {
      if (env.kind !== 'document') return;
      const srcId = (env.payload as DocumentEnvelopePayload | undefined)
        ?.source_artifact_id;
      if (srcId) seenSourceArtifactIds.add(srcId);
    };
    for (const env of items) collect(env);
    for (const list of Object.values(threads)) {
      for (const env of list) collect(env);
    }

    const now = Date.now();
    setPendingUploads(prev => {
      const next = prev.filter(
        p =>
          !seenSourceArtifactIds.has(p.artifactId) &&
          now - p.uploadedAt < PENDING_UPLOAD_TIMEOUT_MS,
      );
      return next.length === prev.length ? prev : next;
    });
  }, [items, threads]);

  const { isDragging } = useChartWideDropZone({
    enabled: enableDropZone && !!patientId,
    onDrop: files => {
      void handleDrop(files);
    },
  });

  const closeDrawer = useCallback(() => setOpenDocRefId(null), []);

  // Generated documents (DocumentMetadata, mongo) and DocRefs (Postgres)
  // both arrive as `kind=document` envelopes but are downloaded through
  // different endpoints. Generated docs have no enrichment so the drawer
  // can't say much; we just hand the user the file directly.
  const [triggerGeneratedDownload] = useLazyGetDocumentDownloadUrlQuery();

  const handleOpenEnvelope = useCallback(
    async (envelope: TimelineEnvelope) => {
      if (envelope.kind !== 'document') return;
      if (isGeneratedDoc(envelope)) {
        try {
          const result = await triggerGeneratedDownload({
            documentId: envelope.id,
          }).unwrap();
          if (result?.download_url) {
            window.open(result.download_url, '_blank', 'noopener,noreferrer');
          }
        } catch {
          // Hook surfaces error state; failure is silent here.
        }
        return;
      }
      // Real DocRef → open the drawer (it fetches detail + sections).
      setOpenDocRefId(envelope.id);
    },
    [triggerGeneratedDownload],
  );

  const isFiltered =
    filterState.lensId !== 'all' ||
    filterState.kindId !== 'all' ||
    filterState.dateId !== 'all' ||
    filterState.query !== '';

  return (
    <Stack spacing={2}>
      <TimelineFilterStrip
        lensFilters={LENS_FILTERS}
        kindFilters={KIND_FILTERS}
        dateFilters={dateFilters}
        kindCounts={kindCounts}
        state={filterState}
        totalShown={totalShown}
        onChange={setFilterState}
      />

      {error && (
        <Alert severity="error">Couldn&apos;t load this patient&apos;s timeline.</Alert>
      )}
      {uploadError != null && (
        <Alert severity="error">Upload failed. Try again.</Alert>
      )}

      {pendingUploads.length > 0 && (
        <Stack spacing={1}>
          {pendingUploads.map(p => (
            <UploadingCard
              key={p.artifactId}
              filename={p.filename}
              status={p.status}
            />
          ))}
        </Stack>
      )}

      {isLoading ? (
        <Stack spacing={1}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rounded"  sx={{ height: 88 }}/>
          ))}
        </Stack>
      ) : items.length === 0 && pendingUploads.length === 0 ? (
        <EmptyState isFiltered={isFiltered} />
      ) : (
        <Stack spacing={2}>
          {grouped.map(group => (
            <Box key={group.key}>
              <PeriodLabel
                label={group.label}
                count={group.items.length}
                sx={{ mb: 0.75 }}
              />
              <Stack spacing={1}>
                {group.items.map(envelope =>
                  envelope.kind === 'encounter' ? (
                    <TimelineThread
                      key={envelope.id}
                      encounter={envelope}
                      childEnvelopes={threads[envelope.id] ?? []}
                      onOpenEnvelope={env => void handleOpenEnvelope(env)}
                    />
                  ) : (
                    <EnvelopeRow
                      key={envelope.id}
                      envelope={envelope}
                      onOpenEnvelope={env => void handleOpenEnvelope(env)}
                    />
                  ),
                )}
              </Stack>
            </Box>
          ))}

          <Stack
            direction="row"
            spacing={1}
            sx={{alignItems: "center",  pt: 1, color: 'text.secondary' }}
          >
            {(isUploading || isFetching || pendingUploads.length > 0) && (
              <CircularProgress size={12} />
            )}
            <Typography variant="caption">
              {totalShown} item{totalShown === 1 ? '' : 's'} shown
              {pendingUploads.length > 0
                ? ` · ${pendingUploads.length} processing…`
                : ''}
            </Typography>
          </Stack>
        </Stack>
      )}

      {isDragging && <ArtifactDropOverlay patientName={patientName} />}

      <DocRefDrawer
        documentReferenceId={openDocRefId}
        onClose={closeDrawer}
      />
    </Stack>
  );
}

function PeriodLabel({
  label,
  count,
  sx,
}: {
  label: string;
  count: number;
  sx?: React.ComponentProps<typeof Stack>['sx'];
}) {
  const stackSx: SxProps<Theme> = [
    { alignItems: 'center' },
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ];

  return (
    <Stack direction="row" spacing={1} sx={stackSx}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontSize: 10, letterSpacing: 1 }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {count}
      </Typography>
      <Box sx={{ flex: 1, borderBottom: '1px solid', borderColor: 'divider' }} />
    </Stack>
  );
}

function EmptyState({ isFiltered }: { isFiltered: boolean }) {
  return (
    <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
      <Typography variant="body2" gutterBottom>
        {isFiltered
          ? 'Nothing matches these filters.'
          : 'Nothing on the timeline yet.'}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        Drop a file anywhere on this page to add a record.
      </Typography>
    </Box>
  );
}
